import { Box, Button, CircularProgress, Typography, Card, CardContent, Divider, Grid } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { useAuth0 } from "@auth0/auth0-react";
import { createCheckoutSession } from "src/api/createCheckoutSession";
import { useContext } from "react";
import { SidebarContext } from "src/contexts/SidebarContext";

const CurrentPlanUsage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { clientInfo, remainingRequests, isDataLoaded } = useContext(SidebarContext);

  const totalRequests: number = remainingRequests?.total_credits ?? 100;
  const remainingRequestsValue: number = remainingRequests?.remaining_credits ?? 100;
  const usedRequests: number = totalRequests - remainingRequestsValue;

  const handleUpgrade = async () => {
    try {
      const accessAuthToken = await getAccessTokenSilently();

      if (!clientInfo?.clientId) {
        throw new Error("Client ID is not available");
      }

      const response = await createCheckoutSession(accessAuthToken, clientInfo.clientId);

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        console.error("No checkout_url in response");
      }
    } catch (error) {
      console.error("Error creating checkout session", error);
    }
  };

  const scaledMax = 50; // white
  const scaledUsed = (usedRequests / totalRequests) * scaledMax; // orange

  const circleSize = 150;

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-around"
      sx={{
        background: `${PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100]}`,
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
        borderRadius: 0.5,
        padding: 1,
        minHeight: { lg: "275px", md: "235px", sm: "200px", xs: "175px" },
        height: "100%",
      }}
    >
      <Card
        sx={{
          backgroundColor: `${PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100]}`,
          boxShadow: "none",
          borderRadius: 0.5,
          flex: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-evenly",
          alignItems: "center",
        }}
      >
        <CardContent sx={{ padding: "0px !important" }}>
          {/* Medio círculo */}
          <Box
            sx={{
              width: circleSize,
              height: circleSize / 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "baseline",
              position: "relative",
              margin: "0 auto",
              mb: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: circleSize,
                height: circleSize,
                transform: "rotate(-90deg)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Circular progress white */}
              <CircularProgress
                variant="determinate"
                value={scaledMax}
                size={circleSize}
                thickness={5}
                sx={{
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />

              {/* Circular progress orange */}
              {isDataLoaded && (
                <CircularProgress
                  variant="determinate"
                  value={scaledUsed}
                  size={circleSize}
                  thickness={5}
                  sx={{
                    color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
              )}

              <Typography
                variant="h4"
                fontFamily="Roboto, Arial"
                color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
                sx={{
                  textAlign: "center",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-30%, -50%) rotate(90deg)",
                }}
              >
                Requests {isDataLoaded ? `${usedRequests}/${totalRequests}` : `0/${totalRequests}`}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        {/* Upgrade button */}
        <Box display="flex" justifyContent="center">
          <Box p={1}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleUpgrade}
              sx={{
                fontFamily: "Roboto, Arial",
              }}
            >
              UPGRADE
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default CurrentPlanUsage;
